import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, Plus, Eye, EyeOff, Edit, Trash2 } from "lucide-react";
import { useBlogPostStore } from "@/stores/blogPostStore";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuthorStore } from "@/stores/authorStore";
import PermissionGuard from "@/components/PermissionGuard";
import { useDebounce } from "@/hooks/useDebounce";
import { BlogPost } from "@/types/blog-post";
import { ROUTES } from "@/constants/routes";
// import { showToast } from "@/lib/toast";

// Modal components
import { ModalDeleteBlogPost } from "@/components/blog-post/ModalDeleteBlogPost";
// import { ModalEditBlogPost } from "@/components/blog-post/ModalEditBlogPost";
// import { ModalViewBlogPost } from "@/components/blog-post/ModalViewBlogPost";

export default function ArticlePage() {
  const navigate = useNavigate();
  const {
    blogPosts,
    pagination,
    isLoading,
    fetchBlogPosts,
    setCurrentPage,
    setSelectedBlogPost,
    clearSelectedBlogPost,
    toggleDisplay,
  } = useBlogPostStore();

  const { canRead } = usePermissions();
  const { authors, fetchAuthors } = useAuthorStore();

  // Search and filters
  const [searchTerm, setSearchTerm] = useState("");
  const [visibilityFilter, setVisibilityFilter] = useState<string>("_all");
  const [authorFilter, setAuthorFilter] = useState<string>("_all");

  const debouncedSearch = useDebounce(searchTerm, 500);
  const [, startTransition] = useTransition();
  const deferredBlogPosts = useDeferredValue(blogPosts);
  const searchRafRef = useRef<number | null>(null);

  // Modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    fetchAuthors({ page: 1, limit: 1000 });
  }, [fetchAuthors]);

  // Build query params memoized
  const queryParams = useMemo(() => {
    const visibilityParam =
      visibilityFilter === "_all"
        ? undefined
        : (visibilityFilter as "public" | "private");
    return {
      page: pagination.page,
      limit: pagination.limit,
      search: debouncedSearch || undefined,
      visibility: visibilityParam,
      author: authorFilter === "_all" ? undefined : authorFilter,
    } as const;
  }, [
    debouncedSearch,
    visibilityFilter,
    authorFilter,
    pagination.page,
    pagination.limit,
  ]);

  useEffect(() => {
    startTransition(() => {
      fetchBlogPosts({ ...queryParams });
    });
  }, [queryParams, fetchBlogPosts]);

  // Handle search
  const handleSearch = useCallback(() => {
    setCurrentPage(1);
  }, [setCurrentPage]);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  useEffect(() => {
    return () => {
      if (searchRafRef.current) {
        cancelAnimationFrame(searchRafRef.current);
        searchRafRef.current = null;
      }
    };
  }, []);

  // Handle pagination
  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
    },
    [setCurrentPage]
  );

  const blogPostsList = useMemo(() => {
    if (isLoading) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Đang tải...</p>
        </div>
      );
    }
    if (deferredBlogPosts.length === 0) {
      return (
        <div className="text-center py-8">
          <div className="text-gray-400 text-lg mb-2">
            Không có blog post nào
          </div>
          <div className="text-gray-500">
            Thử thay đổi bộ lọc hoặc tạo blog post mới
          </div>
        </div>
      );
    }
    return (
      <>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/80 dark:bg-gray-800/80 backdrop-blur supports-[backdrop-filter]:bg-gray-50/60 dark:supports-[backdrop-filter]:bg-gray-800/60">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Hình ảnh
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Đường dẫn
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Tiêu đề
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Mô tả
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Tác giả
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Trạng thái
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Hành động
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
              {deferredBlogPosts.map((blogPost: BlogPost) => {
                const statusLabel = getStatusLabel(blogPost.visibility);
                const statusVariant = getStatusBadgeVariant(
                  blogPost.visibility
                );
                const description = blogPost.pageDescription
                  ? blogPost.pageDescription
                  : blogPost.content.replace(/<[^>]*>/g, "").substring(0, 150) +
                    "...";
                return (
                  <tr
                    key={blogPost._id}
                    className="transition-all hover:bg-gray-50/60 dark:hover:bg-gray-800/60"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      {blogPost.featuredImage?.url ? (
                        <img
                          src={blogPost.featuredImage.url}
                          alt={blogPost.featuredImage.alt || blogPost.title}
                          className="w-14 h-14 object-cover rounded"
                        />
                      ) : (
                        <div className="w-14 h-14 bg-gray-100 rounded" />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 truncate max-w-[240px]">
                      <Link to={`/blog-posts/${blogPost._id}/edit`}>
                        /{blogPost.slug}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900 dark:text-white truncate max-w-[200px]">
                        {blogPost.title}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 truncate max-w-[300px]">
                      <div>{description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <div>{blogPost.author?.name || "-"}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge
                        variant={statusVariant}
                        className={
                          blogPost.visibility === "private"
                            ? "bg-red-200 text-red-700"
                            : "bg-blue-200 text-blue-700"
                        }
                      >
                        {statusLabel}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGuard subject="blog_post" action="read">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleView(blogPost)}
                            className="cursor-pointer"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </PermissionGuard>
                        <PermissionGuard subject="blog_post" action="update">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(blogPost)}
                            className="cursor-pointer"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </PermissionGuard>
                        <PermissionGuard subject="blog_post" action="update">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleDisplay(blogPost)}
                            className="cursor-pointer"
                            title={
                              blogPost.isDisplay
                                ? "Ẩn khỏi tin tức & tìm kiếm (vẫn xem được qua URL)"
                                : "Hiện lại trên tin tức & tìm kiếm"
                            }
                          >
                            {blogPost.isDisplay ? (
                              <Eye className="w-4 h-4" />
                            ) : (
                              <EyeOff className="w-4 h-4 text-orange-500" />
                            )}
                          </Button>
                        </PermissionGuard>
                        <PermissionGuard subject="blog_post" action="delete">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(blogPost)}
                            className="cursor-pointer text-red-600 hover:text-red-700 hover:border-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </PermissionGuard>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button
              variant="outline"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="cursor-pointer"
            >
              Trước
            </Button>

            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(
              (page) => (
                <Button
                  key={page}
                  variant={page === pagination.page ? "default" : "outline"}
                  onClick={() => handlePageChange(page)}
                  className="cursor-pointer"
                >
                  {page}
                </Button>
              )
            )}

            <Button
              variant="outline"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className="cursor-pointer"
            >
              Sau
            </Button>
          </div>
        )}
      </>
    );
  }, [deferredBlogPosts, isLoading, pagination, handlePageChange]);

  // Handle actions
  const handleView = (blogPost: BlogPost) => {
    setSelectedBlogPost(blogPost);
    // TODO: Implement view modal
  };

  const handleEdit = (blogPost: BlogPost) => {
    navigate(`/blog-posts/${blogPost._id}/edit`);
  };

  const handleDelete = (blogPost: BlogPost) => {
    setSelectedBlogPost(blogPost);
    setIsDeleteModalOpen(true);
  };

  const handleToggleDisplay = (blogPost: BlogPost) => {
    toggleDisplay(blogPost._id);
  };

  // Get status badge variant
  function getStatusBadgeVariant(status: string) {
    switch (status) {
      case "public":
        return "default";
      case "private":
        return "secondary";
      default:
        return "secondary";
    }
  }

  // Get status label
  function getStatusLabel(status: string) {
    switch (status) {
      case "public":
        return "Công khai";
      case "private":
        return "Riêng tư";

      default:
        return status;
    }
  }

  // Check if user can access page
  if (!canRead("blog_post")) {
    return (
      <DashboardLayout>
        <div className="p-10 text-center">
          <div className="text-red-600 text-lg font-medium mb-2">
            Không có quyền truy cập
          </div>
          <div className="text-gray-500">
            Bạn không có quyền truy cập trang quản lý blog post
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600">
                Quản lý Blog Post
              </h1>
              <div className="mt-2 flex items-center gap-2">
                <p className="text-gray-500 dark:text-gray-400">
                  Quản lý và xuất bản các blog post trên website
                </p>
                <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
                  {pagination.total} blog post
                </span>
              </div>
            </div>
            <PermissionGuard subject="blog_post" action="create">
              <Button
                onClick={() => navigate(ROUTES.CREATE_BLOG_POST)}
                className="bg-blue-600 hover:bg-blue-700 cursor-pointer"
              >
                <Plus className="w-4 h-4 mr-2" />
                Thêm blog post mới
              </Button>
            </PermissionGuard>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Bộ lọc và tìm kiếm</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Tìm kiếm
                </label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Tìm theo tiêu đề, nội dung..."
                    value={searchTerm}
                    onChange={handleSearchInputChange}
                    className="flex-1"
                    type="text"
                  />
                  <Button onClick={handleSearch} className="cursor-pointer">
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Hiển thị
                </label>
                <Select
                  value={visibilityFilter}
                  onValueChange={(v) => setVisibilityFilter(v)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Tất cả" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Tất cả</SelectItem>
                    <SelectItem value="public">Công khai</SelectItem>
                    <SelectItem value="private">Riêng tư</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Tác giả
                </label>
                <Select
                  value={authorFilter}
                  onValueChange={(v) => setAuthorFilter(v)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Tất cả tác giả" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Tất cả tác giả</SelectItem>
                    {authors.map((a) => (
                      <SelectItem key={a._id} value={a._id}>
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Articles List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                Danh sách Blog Post
                <Badge variant="secondary" className="ml-2">
                  {pagination.total} blog post
                </Badge>
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>{blogPostsList}</CardContent>
        </Card>

        {/* Modals */}
        <ModalDeleteBlogPost
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            clearSelectedBlogPost();
          }}
          onComplete={() => {
            setIsDeleteModalOpen(false);
            clearSelectedBlogPost();
            // Refresh blog posts
            fetchBlogPosts({
              page: pagination.page,
              limit: pagination.limit,
              search: debouncedSearch || undefined,
              visibility:
                visibilityFilter === "_all"
                  ? undefined
                  : (visibilityFilter as "public" | "private"),
              author: authorFilter === "_all" ? undefined : authorFilter,
            });
          }}
        />

        {/* <ModalEditBlogPost
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            clearSelectedBlogPost();
          }}
          onComplete={() => {
            setIsEditModalOpen(false);
            clearSelectedBlogPost();
            // Refresh blog posts
            fetchBlogPosts({
              page: pagination.page,
              limit: pagination.limit,
              search: debouncedSearch || undefined,
              visibility:
                visibilityFilter === "_all"
                  ? undefined
                  : (visibilityFilter as "public" | "private"),
              author: authorFilter === "_all" ? undefined : authorFilter,
            });
          }}
        />

        <ModalViewBlogPost
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            clearSelectedBlogPost();
          }}
        /> */}
      </div>
    </DashboardLayout>
  );
}
